<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/interactions';
import typesDocs from 'docs:@react-types/shared/src/events.d.ts';
import {HeaderInfo, FunctionAPI, TypeContext, InterfaceType, TypeLink} from '@react-spectrum/docs';
import packageData from '@react-aria/interactions/package.json';

```jsx import
import {useFocusWithin} from '@react-aria/interactions';
```

---
category: Interactions
keywords: [keyboard focus, focus management, focus ring, focus descendant, aria]
---

# useFocusWithin

<p>{docs.exports.useFocusWithin.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useFocusWithin']} />

## API

<FunctionAPI function={docs.exports.useFocusWithin} links={docs.links} />

## Features

`useFocusWithin` handles focus interactions for an element and its descendants. Focus is "within"
an element when either the element itself or a descendant element has focus. This is similar to
the [:focus-within](https://developer.mozilla.org/en-US/docs/Web/CSS/:focus-within) pseudo class
in CSS.

To handle focus events on only the target element, and not descendants, see [useFocus](useFocus.html).

## Usage

`useFocusWithin` returns props that you should spread onto the target element:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useFocusWithin.return.id].properties} />
</TypeContext.Provider>

`useFocusWithin` supports the following event handlers:

<!-- filter props to only include events... -->
<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={Object.fromEntries(Object.entries(docs.links[docs.exports.useFocusWithin.parameters[0].value.id].properties).filter(([, prop]) => prop.value.type === 'function'))} />
</TypeContext.Provider>

## Example

This example shows two text fields inside a div, which handles focus within events. It stores focus
within state in local component state, which is updated by an `onFocusWithinChange` handler. This is used
to update the background color and text color of the group while one of the text fields has focus.

Focus within and blur within events are also logged to the list below. Notice that the events are only
fired when the wrapper gains and loses focus, not when focus moves within the group.

**NOTE: for more advanced text field functionality, see [useTextField](useTextField.html).**

```tsx example
function Example() {
  let [events, setEvents] = React.useState([]);
  let [isFocusWithin, setFocusWithin] = React.useState(false);
  let {focusWithinProps} = useFocusWithin({
    onFocusWithin: e => setEvents(
      events => [...events, 'focus within']
    ),
    onBlurWithin: e => setEvents(
      events => [...events, 'blur within']
    ),
    onFocusWithinChange: isFocusWithin => setFocusWithin(isFocusWithin)
  });

  return (
    <>
      <div
        {...focusWithinProps}
        style={{
          display: 'inline-block',
          border: '1px solid gray',
          padding: 10,
          background: isFocusWithin ? 'goldenrod' : '',
          color: isFocusWithin ? 'black' : ''
        }}>
        <label style={{display: 'block'}}>
          First Name: <input />
        </label>
        <label style={{display: 'block'}}>
          Last Name: <input />
        </label>
      </div>
      <ul>
        {events.map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    </>
  );
}
```
